import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  X as Close,
  Trash,
  ArrowsClockwise as Swap,
  FilePlus,
  WarningCircle,
} from '@phosphor-icons/react';
import '../../../lib/pdfWorkerSetup'; // Initialize PDF worker
import { Document, Page } from 'react-pdf';
import type { ContractField, PdfPageInfo } from '../../../types';
import { api } from '../../../lib/api';
import { authService } from '../../../lib/auth';

const THUMBNAIL_WIDTH = 170;

export interface PdfPageChange {
  pageInfo: PdfPageInfo[];
  fields: ContractField[];
}

interface PdfPageManagerProps {
  open: boolean;
  onClose: () => void;
  /** Authenticated view_pdf endpoint for the document being edited. */
  pdfUrl: string;
  /** Contract id, or template id when isTemplate is set. */
  documentId: number | string;
  isTemplate?: boolean;
  title?: string;
  /**
   * Fired after every successful page edit with the server's new page metadata
   * and field list. The fields come back renumbered, so callers must adopt them
   * rather than keeping the copy they had.
   */
  onPagesChanged: (change: PdfPageChange) => void;
}

type PendingAction = { kind: 'delete' | 'replace' | 'append'; page?: number } | null;

/**
 * Delete, replace, and merge pages of a contract PDF.
 *
 * Until this existed the only PDF operation was replacing the whole document:
 * one bad page meant re-exporting everything, and a contract that needed a
 * second source file could not be assembled at all.
 */
export const PdfPageManager: React.FC<PdfPageManagerProps> = ({
  open,
  onClose,
  pdfUrl,
  documentId,
  isTemplate = false,
  title = 'Manage Pages',
  onPagesChanged,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [confirmDeletePage, setConfirmDeletePage] = useState<number | null>(null);
  // Bumped after every successful edit. The stored PDF is rewritten in place at
  // the same URL, so without this the thumbnails would keep showing the document
  // as it was before the edit.
  const [reloadToken, setReloadToken] = useState(0);

  const blobUrlRef = useRef<string | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const appendInputRef = useRef<HTMLInputElement>(null);
  // Which page the file picker was opened for; the input's onChange has no
  // other way back to it.
  const replaceTargetRef = useRef<number | null>(null);

  const basePath = isTemplate
    ? `/api/crm/contract-templates/${documentId}`
    : `/api/crm/contracts/${documentId}`;

  // Fetch the PDF with the bearer token - view_pdf is authenticated, so
  // react-pdf cannot load it directly.
  React.useEffect(() => {
    const releaseBlobUrl = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    if (!open || !pdfUrl) {
      releaseBlobUrl();
      setPdfBlobUrl(null);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setError(null);
      // Cleared so no thumbnail asks the incoming document for a page index
      // only the outgoing one had; it doubles as the loading flag.
      setNumPages(null);

      try {
        const accessToken = authService.getAccessToken();
        const separator = pdfUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${pdfUrl}${separator}r=${reloadToken}`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        releaseBlobUrl();
        blobUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
      }
    };

    load();

    return () => controller.abort();
  }, [open, pdfUrl, reloadToken]);

  // Release the last object URL when the manager goes away for good
  React.useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    },
    []
  );

  const applyResult = useCallback(
    (data: any) => {
      setReloadToken((token) => token + 1);
      onPagesChanged({
        pageInfo: data?.page_info || [],
        fields: data?.fields || [],
      });
    },
    [onPagesChanged]
  );

  const errorFrom = (err: any, fallback: string): string =>
    err?.responseData?.error || err?.message || fallback;

  const handleDelete = useCallback(
    async (page: number) => {
      setPending({ kind: 'delete', page });
      setError(null);
      try {
        const response = await api.post<any>(`${basePath}/delete-page/`, { page });
        if (response.status !== 200) {
          throw new Error(response.data?.error || 'Failed to delete page');
        }
        applyResult(response.data);
      } catch (err: any) {
        setError(errorFrom(err, 'Failed to delete page'));
      } finally {
        setPending(null);
        setConfirmDeletePage(null);
      }
    },
    [applyResult, basePath]
  );

  const handleReplaceFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const page = replaceTargetRef.current;
      // Clear the input so re-picking the same filename still fires onChange.
      event.target.value = '';
      replaceTargetRef.current = null;
      if (!file || !page) return;

      setPending({ kind: 'replace', page });
      setError(null);
      try {
        const formData = new FormData();
        formData.append('pdf_file', file);
        formData.append('page', String(page));
        const response = await api.post<any>(`${basePath}/replace-page/`, formData);
        if (response.status !== 200) {
          throw new Error(response.data?.error || 'Failed to replace page');
        }
        applyResult(response.data);
      } catch (err: any) {
        setError(errorFrom(err, 'Failed to replace page'));
      } finally {
        setPending(null);
      }
    },
    [applyResult, basePath]
  );

  const handleAppendFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      setPending({ kind: 'append' });
      setError(null);
      try {
        const formData = new FormData();
        formData.append('pdf_file', file);
        const response = await api.post<any>(`${basePath}/append-pdf/`, formData);
        if (response.status !== 200) {
          throw new Error(response.data?.error || 'Failed to add PDF');
        }
        applyResult(response.data);
      } catch (err: any) {
        setError(errorFrom(err, 'Failed to add PDF'));
      } finally {
        setPending(null);
      }
    },
    [applyResult, basePath]
  );

  const busy = pending !== null;
  const isLastPage = numPages === 1;

  return (
    <Modal open={open} onClose={busy ? undefined : onClose}>
      <ModalDialog
        variant="plain"
        sx={{
          width: '95vw',
          maxWidth: 1100,
          height: '90vh',
          p: 0,
          overflow: 'hidden',
          bgcolor: 'background.surface',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.level1',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography level="h4" noWrap>
              {title}
            </Typography>
            {numPages !== null && (
              <Chip size="sm" variant="soft">
                {numPages} page{numPages === 1 ? '' : 's'}
              </Chip>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="solid"
              color="primary"
              size="sm"
              startDecorator={<FilePlus size={16} />}
              onClick={() => appendInputRef.current?.click()}
              loading={pending?.kind === 'append'}
              disabled={busy}
            >
              Add PDF
            </Button>
            <IconButton
              variant="plain"
              color="neutral"
              aria-label="Close page manager"
              onClick={onClose}
              disabled={busy}
            >
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <input
          ref={replaceInputRef}
          type="file"
          accept=".pdf"
          onChange={handleReplaceFile}
          style={{ display: 'none' }}
        />
        <input
          ref={appendInputRef}
          type="file"
          accept=".pdf"
          onChange={handleAppendFile}
          style={{ display: 'none' }}
        />

        {error && (
          <Alert color="danger" startDecorator={<WarningCircle size={18} />} sx={{ m: 2, mb: 0 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ px: 2, pt: 2 }}>
          <Typography level="body-sm" textColor="text.secondary">
            Replace a page with another PDF, delete one you don't need, or merge a second
            document onto the end. Fields placed on a deleted page are removed with it; fields
            on later pages move with them.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* One spinner for both the first load and every reload after an
              edit - numPages is null for the whole of either. */}
          {numPages === null && !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {pdfBlobUrl && (
            <Document
              file={pdfBlobUrl}
              onLoadSuccess={({ numPages: count }) => setNumPages(count)}
              onLoadError={(err) => setError(`Failed to load PDF: ${err.message}`)}
              loading={null}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${THUMBNAIL_WIDTH + 24}px, 1fr))`,
                  gap: 2,
                }}
              >
                {Array.from(new Array(numPages || 0), (_el, index) => {
                  const pageNumber = index + 1;
                  const pageBusy = busy && pending?.page === pageNumber;

                  return (
                    <Sheet
                      key={`page_${pageNumber}`}
                      variant="outlined"
                      sx={{
                        borderRadius: 'sm',
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        position: 'relative',
                        opacity: busy && !pageBusy ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{ position: 'relative', lineHeight: 0 }}>
                        <Page
                          pageNumber={pageNumber}
                          width={THUMBNAIL_WIDTH}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                        {pageBusy && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(255,255,255,0.7)',
                            }}
                          >
                            <CircularProgress size="sm" />
                          </Box>
                        )}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%' }}
                      >
                        <Typography level="body-xs" textColor="text.secondary">
                          Page {pageNumber}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Replace this page">
                            <IconButton
                              size="sm"
                              variant="outlined"
                              color="neutral"
                              aria-label={`Replace page ${pageNumber}`}
                              disabled={busy}
                              onClick={() => {
                                replaceTargetRef.current = pageNumber;
                                replaceInputRef.current?.click();
                              }}
                            >
                              <Swap size={15} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              isLastPage
                                ? 'A contract must keep at least one page'
                                : 'Delete this page'
                            }
                          >
                            <span>
                              <IconButton
                                size="sm"
                                variant="outlined"
                                color="danger"
                                aria-label={`Delete page ${pageNumber}`}
                                disabled={busy || isLastPage}
                                onClick={() => setConfirmDeletePage(pageNumber)}
                              >
                                <Trash size={15} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Sheet>
                  );
                })}
              </Box>
            </Document>
          )}
        </Box>

        {/* Delete confirmation - a page and its fields do not come back */}
        <Modal open={confirmDeletePage !== null} onClose={() => setConfirmDeletePage(null)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <Typography level="title-lg">Delete page {confirmDeletePage}?</Typography>
            <Typography level="body-sm" textColor="text.secondary" sx={{ mt: 1 }}>
              The page is removed from the document and any fields placed on it are deleted.
              This can't be undone.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="plain" color="neutral" onClick={() => setConfirmDeletePage(null)}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                loading={pending?.kind === 'delete'}
                onClick={() => confirmDeletePage && handleDelete(confirmDeletePage)}
              >
                Delete page
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      </ModalDialog>
    </Modal>
  );
};

export default PdfPageManager;
